import React from 'react';
import type { GraphEntity, GraphRelation, GraphDocument } from '../../services/api';

interface EntityDetailProps {
  entity:           GraphEntity | null;
  relations:        GraphRelation[];
  documents:        GraphDocument[];
  onClose:          () => void;
  onSelectRelation: (rel: GraphRelation) => void;
}

const ENTITY_TYPE_COLORS: Record<string, string> = {
  Person:       'bg-blue-600',
  Organization: 'bg-purple-600',
  Place:        'bg-green-600',
  Event:        'bg-orange-600',
  Concept:      'bg-teal-600',
  Value:        'bg-gray-600',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  PERSON:  'Persona',
  ORG:     'Organizzazione',
  PLACE:   'Luogo',
  EVENT:   'Evento',
  CONCEPT: 'Concetto',
  VALUE:   'Valore',
};

const LEVEL_DOT: Record<string, string> = {
  STRONG: 'bg-green-400',
  MEDIUM: 'bg-yellow-400',
  WEAK:   'bg-gray-500',
};

function strengthToLevel(s?: number): 'STRONG' | 'MEDIUM' | 'WEAK' {
  if (s == null || s < 0.4) return 'WEAK';
  if (s >= 0.75) return 'STRONG';
  return 'MEDIUM';
}

export function EntityDetail({ entity, relations, documents, onClose, onSelectRelation }: EntityDetailProps) {
  if (!entity) return null;

  const label      = (entity.labels ?? [])[0] ?? 'Entity';
  const colorClass = ENTITY_TYPE_COLORS[label] ?? 'bg-gray-600';
  const typeLabel  = ENTITY_TYPE_LABELS[entity.entity_type ?? ''] ?? label;

  const directRelations = relations.filter(
    (r) => r.subject === entity.entity_key || r.object === entity.entity_key
  );

  // Group by type, each group sorted by confidence desc
  const grouped = directRelations
    .slice()
    .sort((a, b) => (b.validated_confidence ?? 0) - (a.validated_confidence ?? 0))
    .reduce<Record<string, GraphRelation[]>>((acc, r) => {
      if (!acc[r.type]) acc[r.type] = [];
      acc[r.type].push(r);
      return acc;
    }, {});
  const groupKeys = Object.keys(grouped).sort();

  const aliases = (entity.aliases ?? []).filter(
    (a) => a.toLowerCase() !== (entity.normalized_form ?? '').toLowerCase()
  );

  return (
    <div className="flex flex-col h-full bg-gray-800 rounded-lg text-white text-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h3 className="font-semibold text-base">Profilo entità</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">×</button>
      </div>

      {/* Identity */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}>{typeLabel}</span>
          <span className="font-semibold text-base">{entity.normalized_form || entity.entity_key}</span>
        </div>
        <div className="font-mono text-xs text-gray-500 mt-1">{entity.entity_key}</div>

        {entity.mention_count != null && (
          <div className="text-xs text-gray-400 mt-1">Menzioni: {entity.mention_count}</div>
        )}

        {aliases.length > 0 && (
          <div className="mt-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Alias: </span>
            <span className="text-xs text-gray-300">{aliases.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">

        {/* Relations grouped by type */}
        {groupKeys.length > 0 && groupKeys.map((type) => (
          <div key={type}>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              {type} <span className="text-gray-600 font-normal">({grouped[type].length})</span>
            </h4>
            <div className="flex flex-col gap-1">
              {grouped[type].map((r, i) => {
                const isSubject  = r.subject === entity.entity_key;
                const counterKey = isSubject ? r.object : r.subject;
                const counterName = counterKey.split(':').slice(1).join(':');
                const level      = strengthToLevel(r.strength);
                return (
                  <button
                    key={i}
                    onClick={() => onSelectRelation(r)}
                    className="flex items-center gap-2 text-left p-2 rounded hover:bg-gray-700 transition-colors w-full"
                  >
                    {/* Direction indicator */}
                    <span className="text-gray-600 text-xs shrink-0">{isSubject ? '→' : '←'}</span>

                    {/* Counterpart name */}
                    <span className="text-xs text-gray-300 font-mono truncate flex-1">{counterName}</span>

                    {/* Level dot */}
                    <span className={`w-2 h-2 rounded-full shrink-0 ${LEVEL_DOT[level]}`} title={level} />

                    {/* Confidence */}
                    {r.validated_confidence != null && (
                      <span className="text-xs text-gray-500 shrink-0 tabular-nums">
                        {(r.validated_confidence * 100).toFixed(0)}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Source documents */}
        {documents.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Documenti fonte
            </h4>
            <div className="flex flex-col gap-1.5">
              {documents.map((d) => (
                <div key={d.id} className="text-xs text-gray-300">
                  <div className="font-medium">{d.title || 'Untitled'}</div>
                  <div className="text-gray-500">
                    {d.source_type}{d.published_at && ` · ${new Date(d.published_at).toLocaleDateString('it-IT')}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {groupKeys.length === 0 && documents.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-4">Nessun dato disponibile</p>
        )}
      </div>
    </div>
  );
}
